import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { SimpleTooltip } from "@/components/flowly/infra/SimpleTooltip/SimpleTooltip";
import { CreditCostPill } from "@/components/flowly/CreditCostPill/CreditCostPill";
import { useAnonymousGate } from "@/hooks/useAnonymousGate";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "@/lib/toast";
import { GENERATION_PAID_ONLY } from "@/config/featureFlags";
import { canUseDuringWindDown } from "@/lib/subscriptionUtils";
import type { GenerateButtonProps } from "./types";

export function GenerateButton({
  onGenerate,
  generating,
  disabled,
  creditCost,
  costsLoading,
  buttonLabel,
  buttonTooltip,
  onDisabledClick,
}: GenerateButtonProps) {
  const { profile } = useAuth();
  const { t } = useLanguage();
  // Wind-down: only active subscribers (or admins) may generate. This single
  // button funnels every flow, so disabling it here covers every task form.
  const windDownBlocked = GENERATION_PAID_ONLY && !canUseDuringWindDown(profile);
  const isDisabled = disabled || generating || creditCost < 0 || windDownBlocked;
  // Persistent "ready" state — every step is done and the credit cost is
  // resolved. Drives a continuous +15% scale + halo pulse + 1.5px wobble
  // so the user can't miss that they're now one click from creating.
  const isReady = !isDisabled && !costsLoading;

  // Built-in auth gate: every Generate click across every studio flow funnels
  // through this single button, so wrapping `onGenerate` here makes the
  // signup modal automatic for any (current or future) flow. Anon click →
  // AuthModal opens, onGenerate is skipped. Signed-in click → onGenerate runs
  // normally. Idempotent against flow-level wraps; safe if a hook still
  // wraps its own action (the inner gate just re-checks the same `user`).
  const gatedGenerate = useAnonymousGate(onGenerate);

  const handleClick = () => {
    if (windDownBlocked) {
      toast.error(t.common.serviceWindingDown);
      return;
    }
    if (isDisabled) {
      onDisabledClick?.();
      return;
    }
    gatedGenerate();
  };

  const button = (
    <button
      data-tour="generate-button"
      onClick={handleClick}
      aria-disabled={isDisabled}
      className={cn(
        "flex items-center justify-center gap-2 w-full md:w-auto px-6 md:px-8 h-10 rounded-xl font-bold text-sm md:text-base whitespace-nowrap shrink-0 transition-transform",
        "bg-gradient-to-r gradient-primary text-white",
        isDisabled && "opacity-50 cursor-not-allowed shadow-none",
        // Base shadow + interactive feedback — applied whenever the button
        // is clickable. Mobile keeps this look even when isReady, so the
        // button stays inside the compact bottom GenerationBar instead of
        // poking out (a +15% scale would clip the bar's rounded edges).
        !isDisabled &&
          "shadow-[0_0_20px_hsl(var(--brand)/0.3),0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_0_28px_hsl(var(--brand)/0.5),0_4px_20px_rgba(0,0,0,0.25)] hover:scale-[1.03] active:scale-[0.97]",
        // Desktop-only "ready" animation: continuous +15% scale, halo
        // pulse, 1.5px wobble. The keyframes live in src/index.css under
        // a `@media (min-width: 768px)` block, so this class is a no-op
        // below md — that's how mobile keeps the regular static look.
        // `md:hover:scale-100` neutralizes the hover-scale above so it
        // doesn't fight the animation's transform on desktop.
        isReady && "animate-ready-pulse md:hover:scale-100",
      )}
    >
      {generating ? (
        <RefreshCw className="animate-spin w-5 h-5" />
      ) : (
        <>
          <span>{buttonLabel}</span>
          <CreditCostPill cost={creditCost} loading={costsLoading} />
        </>
      )}
    </button>
  );

  return (
    <SimpleTooltip
      content={windDownBlocked ? t.common.serviceWindingDown : buttonTooltip}
      contentClassName="bg-destructive text-destructive-foreground border-destructive"
    >
      {button}
    </SimpleTooltip>
  );
}
